import {
  CachedPart,
  TimeOutException,
  ValidatedCachedPart,
  ValidatedCachedPartList,
  ValidateList,
} from 'src/store/lists/interfaces'
import BaseServiceProvider from './BaseServiceProvider'
import { LISTS_IMPORT_RETRIES_LIMIT } from '../config/constants'

interface ValidateItemsResponse {
  listBinId: string
}

interface ValidateTransientItemsResponse {
  listId: string
  parts: ValidatedCachedPart[]
}

export interface AddPartsToListResponse {
  totalParts: number
  totalGroups: number
  message: string
}

export interface ListImportResponse {
  listBinId: string
}

class ListValidationServiceProvider extends BaseServiceProvider {
  constructor() {
    super(import.meta.env.VITE_API_BASE_URL, 'list', '1.1', '')
  }

  validateList = async (reqData: ValidateList): Promise<string> => {
    const resp = await this.post<ValidateItemsResponse>(
      'part/validateParts',
      reqData
    )
    const listBinId = resp?.data?.listBinId
    return listBinId
  }

  validateTransientParts = async (
    reqData: ValidateList
  ): Promise<ValidatedCachedPart[]> => {
    const resp = await this.post<ValidateTransientItemsResponse>(
      'part/validateTransientParts',
      reqData
    )
    const validatedParts = resp?.data?.parts
    return validatedParts
  }

  getValidatedParts = async (
    listBinId: string,
    start: number,
    limit: number
  ): Promise<ValidatedCachedPartList> => {
    const resp = await this.get<ValidatedCachedPartList>(
      `/part/${listBinId}/parts?start=${start}&limit=${limit}`
    )
    return resp.data
  }

  updatePart = async (
    listBinId: string,
    seqno: number,
    part: CachedPart
  ): Promise<void> => {
    await this.put<ValidatedCachedPartList>(
      `/part/${listBinId}/parts/${seqno}`,
      part
    )
  }

  addNewParts = async (
    listBinId: string,
    parts: CachedPart[]
  ): Promise<ValidatedCachedPart[]> => {
    const resp = await this.post<ValidatedCachedPartList>(
      `/part/${listBinId}/parts/`,
      parts
    )

    return resp.data?.parts
  }

  addPartsToList = async (
    listId: number,
    listBinId: string
  ): Promise<AddPartsToListResponse> => {
    const url = `/part/${listId}/addPartsToGroups/${listBinId}`
    const resp = await this.post<AddPartsToListResponse>(url)

    return resp.data
  }

  validateFile = async (listId: number, file: File): Promise<string> => {
    const formData = new FormData()
    formData.append('file', file)
    const config = {
      headers: {
        'content-type': 'multipart/form-data',
      },
      timeout: LISTS_IMPORT_RETRIES_LIMIT,
    }

    try {
      const resp = await this.post<ListImportResponse>(
        `part/${listId}/validateImport`,
        formData,
        config
      )
      const listBinId = resp.data.listBinId
      return listBinId
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (e.code === 'ECONNABORTED') {
        const msg = 'theServerDidNotRespondOnTimePleaseTryAgainInAFewMoments'
        throw new TimeOutException(msg)
      } else {
        throw e
      }
    }
  }

  removePart = async (listBinId: string, seqno: number): Promise<void> => {
    await this.delete<void>(`/part/${listBinId}/parts/${seqno}`)
  }

  cleanCache = async (listBinId: string): Promise<void> => {
    if (!listBinId || listBinId === '') return
    await this.delete<void>(`/part/${listBinId}/parts/`)
  }

  getPart = async (
    listBinId: string,
    seqno: number
  ): Promise<ValidatedCachedPart> => {
    const url = `/part/${listBinId}/parts/${seqno}`
    const part = await this.get<ValidatedCachedPart>(url)
    return part?.data
  }
}

export default new ListValidationServiceProvider()
